import { Button, Dropdown, DropdownItem, DropdownItemContent } from '@ads/components-react';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import langEnglish from '@/assets/svg/lang-english-icon.svg';
import langPortuguese from '@/assets/svg/lang-portuguese-icon.svg';
import langSpanish from '@/assets/svg/lang-spanish-icon.svg';
import logoMbx from '@/assets/svg/logo-mbx.svg';
import logoMbxDark from '@/assets/svg/logo-mbxDark.svg';
import logoPecege from '@/assets/svg/logo-pecege.svg';
import { LoginForm } from '@/features/auth';
import { useSession } from '@/shared/store/hooks/useSession';
import { useTheme } from '@/shared/store/hooks/useTheme';
import { Spinner } from '@/shared/ui/atoms';

export const Route = createFileRoute('/login/')({
  component: LoginPage,
});

/**
 * URL/arquivo do vídeo exibido em loop no painel esquerdo.
 * NOTE (login): URL pública está hardcoded.
 */
const HERO_VIDEO_SRC =
  'https://stmovessodev.blob.core.windows.net/home-videos/Banner%2001%20-Banners%20Curso%20-%20Site%20MBX%20-%20Square.mp4';

/** Idiomas disponíveis no seletor do topo. */
const LANGUAGES = [
  { code: 'pt-BR', icon: langPortuguese, label: 'PT | BR' },
  { code: 'en-US', icon: langEnglish, label: 'EN | US' },
  { code: 'es-ES', icon: langSpanish, label: 'ES | ES' },
] as const;

function LoginPage() {
  const { t, i18n } = useTranslation('auth');
  const { isAuthenticated } = useSession();
  const { theme } = useTheme();
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  if (isAuthenticated) {
    return <Navigate to="/" />;
  }

  const currentLanguage =
    LANGUAGES.find(lang => i18n.language.startsWith(lang.code.slice(0, 2))) ?? LANGUAGES[0];

  return (
    <div className="flex min-h-screen bg-ctx-layout-surface">
      {/* Painel do vídeo — visível apenas em telas lg+ */}
      <div className="relative hidden overflow-hidden bg-base-gray-900 lg:block lg:w-[58%]">
        {HERO_VIDEO_SRC ? (
          <>
            {/*
             * Fundo desfocado — preenche as "sobras" quando o vídeo principal
             * passa a ser contido em telas extremamente grandes (>= 1920px).
             */}
            <video
              className="absolute inset-none hidden h-full w-full scale-110 object-cover blur-[40px] min-[1920px]:block"
              src={HERO_VIDEO_SRC}
              autoPlay
              loop
              muted
              playsInline
              tabIndex={-1}
              aria-hidden="true"
            />

            {/* Vídeo principal — cover por padrão, contido em telas gigantes */}
            <video
              className="absolute inset-none h-full w-full object-cover min-[1920px]:object-contain"
              src={HERO_VIDEO_SRC}
              autoPlay
              loop
              muted
              playsInline
              onCanPlay={() => setIsVideoLoaded(true)}
              onError={() => setIsVideoLoaded(true)}
              aria-label={t('login.videoLabel')}
            />

            {/* Spinner de carregamento — some com fade quando o vídeo está pronto */}
            <div
              aria-hidden={isVideoLoaded}
              className={`absolute inset-none z-[3] flex items-center justify-center transition-opacity duration-500 ${
                isVideoLoaded ? 'pointer-events-none opacity-transparent' : 'opacity-opaque'
              }`}
            >
              <Spinner
                size="lg"
                className="!h-xl !w-xl !border-base-neutral-white !border-t-base-neutral-none"
              />
            </div>

            {/* Scrim para legibilidade do rodapé */}
            <div
              className="absolute inset-x-none bottom-none z-[1] h-[12rem] bg-gradient-to-t from-base-neutral-black/deep to-base-neutral-none"
              aria-hidden="true"
            />

            {/* Rodapé sobre o vídeo */}
            <div className="absolute inset-x-none bottom-none z-[2] flex flex-col gap-quark p-lg">
              <div className="flex items-center gap-nano">
                <span className="text-ctx-content-titleAlternative ts-subtitle-xxs">
                  {t('login.footer.distributedBy')}
                </span>
                <img src={logoPecege} alt="pecege" className="h-[1.0625rem] w-auto" />
              </div>
              <span className="text-ctx-content-titleAlternative ts-caption-xxs">
                {t('login.footer.copyright', {
                  year: new Date().getFullYear(),
                })}
              </span>
            </div>
          </>
        ) : null}
      </div>

      {/* Painel do formulário */}
      <div className="relative flex w-full flex-col px-md py-lg lg:w-[42%]">
        {/* Seletor de idioma */}
        <div className="flex justify-end">
          <Dropdown
            align="end"
            trigger={
              <Button
                leadingIcon={() => (
                  <img src={currentLanguage.icon} alt="" className="h-[1.25rem] w-[1.25rem]" />
                )}
                hierarchy="secondary"
              >
                {currentLanguage.label}
              </Button>
            }
          >
            {LANGUAGES.map(lang => (
              <DropdownItem
                key={lang.code}
                onSelect={() => void i18n.changeLanguage(lang.code)}
                status={i18n.language.startsWith(lang.code.slice(0, 2)) ? 'active' : 'default'}
              >
                <DropdownItemContent>
                  <span className="flex items-center gap-nano">
                    <img src={lang.icon} alt="" className="h-[1.25rem] w-[1.25rem]" />
                    {lang.label}
                  </span>
                </DropdownItemContent>
              </DropdownItem>
            ))}
          </Dropdown>
        </div>

        {/* Conteúdo central */}
        <div className="flex flex-1 flex-col justify-center">
          <div className="mx-auto w-full max-w-[30rem] flex flex-col items-center justify-center gap-xs">
            <div className="flex flex-col items-center gap-micro">
              <img
                src={theme === 'dark' ? logoMbxDark : logoMbx}
                alt="MBX"
                className="h-xl w-auto"
              />
              <h2 className="text-ctx-content-title ts-heading-sm">{t('login.title')}</h2>
            </div>

            <LoginForm />
          </div>
        </div>
      </div>
    </div>
  );
}
